/**
 * <asd-countdown data-end="..."> ticks down to a deadline.
 *
 * data-end accepts either epoch seconds ("1792094400") or an ISO 8601 string with a UTC offset
 * ("2026-10-15T20:00:00-04:00"). Without an offset a date string is read in the visitor's timezone,
 * so the offset should always be included.
 *
 * When the deadline passes the clock is replaced by the [data-cd-expired] message, or the whole
 * element (and its nearest [data-cd-host] wrapper) is hidden if there is no message. Dispatches `asd-countdown:expired` (bubbles).
 */
class AsdCountdown extends HTMLElement {
  #end = NaN;
  /** @type {number | undefined} */
  #timer;
  /** @type {number} */
  #lastAnnounced = -1;

  connectedCallback() {
    const raw = (this.dataset.end ?? '').trim();
    this.#end = /^\d+$/.test(raw) ? Number(raw) * 1000 : Date.parse(raw);

    if (Number.isNaN(this.#end)) {
      this.hidden = true;
      return;
    }

    document.addEventListener('visibilitychange', this.#tick);
    this.#tick();
    this.#timer = window.setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    document.removeEventListener('visibilitychange', this.#tick);
  }

  #tick = () => {
    const remaining = this.#end - Date.now();

    if (remaining <= 0) {
      this.#expire();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#set('days', days);
    this.#set('hours', hours);
    this.#set('minutes', minutes);
    this.#set('seconds', seconds);

    this.dataset.state = 'running';

    // Screen readers get a once-a-minute summary rather than a per-second stream.
    const minuteKey = Math.floor(totalSeconds / 60);
    if (minuteKey !== this.#lastAnnounced) {
      this.#lastAnnounced = minuteKey;
      const sr = this.querySelector('[data-cd-sr]');
      if (sr) sr.textContent = `${days} days, ${hours} hours, ${minutes} minutes remaining`;
    }
  };

  /**
   * @param {string} unit
   * @param {number} value
   */
  #set(unit, value) {
    const el = this.querySelector(`[data-cd="${unit}"]`);
    if (!el) return;
    const text = String(value).padStart(2, '0');
    if (el.textContent !== text) el.textContent = text;
  }

  #expire() {
    window.clearInterval(this.#timer);
    if (this.dataset.state === 'expired') return;
    this.dataset.state = 'expired';

    const message = this.querySelector('[data-cd-expired]');
    const clock = this.querySelector('[data-cd-clock]');
    if (clock instanceof HTMLElement) clock.hidden = true;

    if (message instanceof HTMLElement && message.textContent?.trim()) {
      message.hidden = false;
    } else {
      this.hidden = true;
      // Let a wrapping section/block (marked data-cd-host) disappear with it.
      this.closest('[data-cd-host]')?.setAttribute('hidden', '');
    }

    this.dispatchEvent(new CustomEvent('asd-countdown:expired', { bubbles: true }));
  }
}

if (!customElements.get('asd-countdown')) {
  customElements.define('asd-countdown', AsdCountdown);
}
